// Loss vs packet size (owned by the web:charts agent).
//
// One line per node of loss % against packet size, with an automatically
// detected "MTU cliff" — the size at which loss jumps sharply, the classic
// signature of fragmentation or an MTU black hole. The cliff is drawn as a
// dashed vertical marker with a label. Loss values are treated as percentages.

import { useMemo } from 'react';
import uPlot from 'uplot';
import type { NodeInfo, SweepResult } from '@/engine/types';
import { UplotChart } from './Uplot';
import { EmptyNote } from './Canvas';
import { categoricalColors, useChartTheme, type ChartPalette } from './palette';

export interface LossVsSizeProps {
  sweep: SweepResult;
  nodes?: NodeInfo[];
  height?: number;
}

interface Prepared {
  data: uPlot.AlignedData;
  nodeIds: string[];
  sizes: number[];
  cliff: number | null;
}

function prepare(sweep: SweepResult): Prepared {
  const sizes = [...new Set(sweep.cells.map((c) => c.size))].sort((a, b) => a - b);
  const nodeIds = [...new Set(sweep.cells.map((c) => c.nodeId))];
  // node -> size -> {sum,n}
  const acc = new Map<string, Map<number, { s: number; n: number }>>();
  for (const cell of sweep.cells) {
    let row = acc.get(cell.nodeId);
    if (!row) {
      row = new Map();
      acc.set(cell.nodeId, row);
    }
    const a = row.get(cell.size) ?? { s: 0, n: 0 };
    a.s += cell.run.metrics.totalLossPct;
    a.n += 1;
    row.set(cell.size, a);
  }
  const series: (number | null)[][] = nodeIds.map((id) => {
    const row = acc.get(id);
    return sizes.map((sz) => {
      const a = row?.get(sz);
      return a && a.n ? a.s / a.n : null;
    });
  });

  // MTU cliff: largest positive jump in the cross-node mean loss between
  // adjacent sizes, only counted if it climbs above 1%.
  const meanAt = sizes.map((_, i) => {
    let s = 0;
    let n = 0;
    for (const ser of series) {
      const v = ser[i];
      if (v != null) {
        s += v;
        n += 1;
      }
    }
    return n ? s / n : 0;
  });
  let cliff: number | null = null;
  let bestJump = 0;
  for (let i = 1; i < sizes.length; i++) {
    const jump = meanAt[i] - meanAt[i - 1];
    if (jump > bestJump && meanAt[i] > 1) {
      bestJump = jump;
      cliff = sizes[i];
    }
  }

  const data = [sizes, ...series] as uPlot.AlignedData;
  return { data, nodeIds, sizes, cliff };
}

function cliffPlugin(palette: ChartPalette, cliff: number): uPlot.Plugin {
  const draw = (u: uPlot) => {
    const x = u.valToPos(cliff, 'x', true);
    const ctx = u.ctx;
    ctx.save();
    ctx.strokeStyle = palette.lostUp;
    ctx.setLineDash([5, 4]);
    ctx.lineWidth = window.devicePixelRatio || 1;
    ctx.beginPath();
    ctx.moveTo(x, u.bbox.top);
    ctx.lineTo(x, u.bbox.top + u.bbox.height);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = palette.lostUp;
    ctx.font = `${11 * (window.devicePixelRatio || 1)}px system-ui, sans-serif`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'top';
    ctx.fillText(`MTU cliff ~${cliff}B`, x - 4, u.bbox.top + 2);
    ctx.restore();
  };
  return { hooks: { draw } };
}

export function LossVsSize({ sweep, nodes, height = 240 }: LossVsSizeProps) {
  const { palette } = useChartTheme();
  const prep = useMemo(() => prepare(sweep), [sweep]);
  const colors = categoricalColors(palette);
  const nameOf = (id: string) => nodes?.find((n) => n.id === id)?.name ?? id;

  const options = useMemo<Omit<uPlot.Options, 'width'>>(() => {
    const series: uPlot.Series[] = [{ label: 'size' }];
    prep.nodeIds.forEach((id, i) => {
      series.push({
        label: nameOf(id),
        stroke: colors[i % colors.length],
        width: 1.75,
        points: { show: true, size: 5 },
        spanGaps: true,
      });
    });
    return {
      height,
      legend: { show: true },
      cursor: { focus: { prox: 24 } },
      scales: { x: { time: false }, y: { auto: true } },
      axes: [
        {
          stroke: palette.axis,
          grid: { stroke: palette.grid },
          font: '11px system-ui, sans-serif',
          label: 'Packet size (bytes)',
          labelFont: '12px system-ui, sans-serif',
        },
        {
          stroke: palette.axis,
          grid: { stroke: palette.grid },
          font: '11px system-ui, sans-serif',
          label: 'Loss (%)',
          labelFont: '12px system-ui, sans-serif',
        },
      ],
      series,
      plugins: prep.cliff != null ? [cliffPlugin(palette, prep.cliff)] : [],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [palette, height, prep, nodes]);

  if (prep.sizes.length === 0) return <EmptyNote label="No size sweep to chart." />;

  return (
    <figure style={{ margin: 0 }}>
      <div style={{ overflowX: 'auto' }}>
        <UplotChart options={options} data={prep.data} ariaLabel="Loss versus packet size" />
      </div>
      {prep.cliff != null && (
        <figcaption style={{ fontSize: 12, color: palette.textMuted, marginTop: 4 }}>
          Detected MTU cliff at ~{prep.cliff} bytes — loss climbs sharply beyond this size,
          consistent with fragmentation or an MTU black hole.
        </figcaption>
      )}
    </figure>
  );
}
